import { Observable, tap } from 'rxjs';
import { JobStage, JobState, JobStatus, JobType, JobsApi, PagedJobStagesResponse, PagedJobStatusResponse } from 'src/app/hickory-api';
import { MockMode } from 'src/utility/mock-mode';
import { mockHandler } from '../mock.handler';

export class MockJobsApi extends JobsApi {
  public static JobStatusFactory = (tenantId: string, type: JobType, state: JobState) => {
    return {
      id: '123',
      isSuccess: state === 'Success',
      state,
      type,
      tenantId,
      timestamp: new Date(),
      metadata: {
        statistics: {
          TotalStages: 4,
        },
      },
    } as JobStatus;
  };

  public static JobStageFactory = (jobId: string, name?: string) => {
    return {
      id: '123',
      jobId,
      name: name ?? 'Stage 1',
      parentId: null,
      timestamp: new Date(),
      metadata: {
        statistics: {},
      },
    } as JobStage;
  };

  public static AlertPreviewJobRunner = (tenantId: string, processCount: number = 1): (() => Observable<PagedJobStatusResponse>) => {
    const queued = this.JobStatusFactory(tenantId, JobType.AlertPreview, JobState.Queued);
    const running = this.JobStatusFactory(tenantId, JobType.AlertPreview, JobState.Running);
    const success = this.JobStatusFactory(tenantId, JobType.AlertPreview, JobState.Success);
    const defaultPagedResponse: PagedJobStatusResponse = {
      hasErrors: false,
      isValid: true,
      page: 1,
      pageCount: 1,
      pageSize: 1,
      totalItemCount: 1,
    };
    let current = queued;
    let remainingProcesses = processCount;

    return () => {
      switch (current.state) {
        case JobState.Queued:
          return mockHandler({ ...defaultPagedResponse, items: [queued] }, MockMode.HappyDelayed).pipe(
            tap(() => {
              current = running;
            })
          );
        case JobState.Running:
          return mockHandler({ ...defaultPagedResponse, items: [running] }, MockMode.ServerErrorsDelayed).pipe(
            tap(() => {
              remainingProcesses -= 1;
              if (remainingProcesses === 0) {
                remainingProcesses = processCount;
                current = success;
              }
            })
          );
        case JobState.Success:
          return mockHandler({ ...defaultPagedResponse, items: [success] }, MockMode.HappyDelayed).pipe(
            tap(() => {
              current = queued;
            })
          );
      }
    };
  };

  public static AlertPreviewJobStageRunner = (jobId: string): (() => Observable<PagedJobStatusResponse>) => {
    const stageNames = ['Processing', 'Retrieving Map Layers', 'Retrieving Contacts', 'Retrieving ETN Data'];
    const stages = stageNames.map((name) => this.JobStageFactory(jobId, name)).reverse();

    const defaultPagedResponse: PagedJobStagesResponse = {
      hasErrors: false,
      isValid: true,
      page: 1,
      pageCount: 1,
      pageSize: 1,
      totalItemCount: 1,
    };
    let current = 0;
    return () => {
      const ranStages = stages.slice(stages.length - current - 1, stages.length);
      current = (current + 1) % stages.length;
      return mockHandler({ ...defaultPagedResponse, items: ranStages }, MockMode.HappyDelayed);
    };
  };

  public createJob(
    _tenantId: number,
    _type?: any,
    _waitForIndex?: boolean,
    _observe: any = 'body',
    _reportProgress: boolean = false,
    _options?: { httpHeaderAccept?: 'application/json' }
  ): Observable<any> {
    return mockHandler({});
  }

  public getJobEvents(
    _tenantId: number,
    _jobId: string,
    _stageId?: string,
    _page?: number,
    _pageSize?: number,
    _sort?: string,
    _observe?: any,
    _reportProgress?: boolean,
    _options?: { httpHeaderAccept?: 'application/json' }
  ): Observable<any> {
    return mockHandler({});
  }

  public getJobStatuses(
    _tenantId: number,
    _ids?: string[],
    _state?: 'Queued' | 'Running' | 'Success' | 'Failure',
    _type?: any,
    _limitJobHistoryDays?: number,
    _page?: number,
    _pageSize?: number,
    _sort?: string,
    _observe?: any,
    _reportProgress?: boolean,
    _options?: { httpHeaderAccept?: 'application/json' }
  ): Observable<any> {
    return mockHandler({ items: [MockJobsApi.JobStatusFactory(_tenantId?.toString(), _type, 'Success')] });
  }

  public getJobStages(
    _tenantId: number,
    _jobId: string,
    _parentId?: string,
    _ids?: string[],
    _page?: number,
    _pageSize?: number,
    _sort?: string,
    _observe?: any,
    _reportProgress?: boolean,
    _options?: { httpHeaderAccept?: 'application/json' }
  ): Observable<any> {
    return mockHandler({ items: [MockJobsApi.JobStageFactory(_jobId)] });
  }
}
